import {
  createDraftSafeSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { omit } from "lodash";
import {
  ContinueQuotePolicyFragmentFragment,
  UnitConstructionInput,
  UnitDetailsInput,
} from "@covertree/new-business-graphql";
import {
  HomeDetailsForm,
  LocationAndRoofDetailsForm,
} from "@covertree/new-business-forms";

interface HomeDetailsState {
  unitsDetails: {
    construction: UnitConstructionInput;
    details: Omit<UnitDetailsInput, "purchaseDate">;
  }[];
}

const initialState: HomeDetailsState = {
  unitsDetails: [],
};

export const unitsConstructionAndDetailsSlice = createSlice({
  name: "unitsConstructionAndDetails",
  initialState,
  selectors: {
    selectUnitDetails: (state) => state.unitsDetails,
    selectLocationAndRoofDetailsFormDefaultValues(
      state
    ): LocationAndRoofDetailsForm {
      if (state.unitsDetails.length === 0) {
        return {
          roofShape: undefined,
          roofYearYyyy: undefined,
          communityName: undefined,
          location: undefined,
          homeType: undefined,
          homeHudNumber: undefined,
          skirtingType: undefined,
        };
      }

      const { construction, details } = state.unitsDetails[0];

      return {
        ...(construction
          ? {
              roofShape: construction.roofShape ?? undefined,
              roofYearYyyy: construction.roofYearYyyy ?? undefined,
              homeType: construction.homeType ?? undefined,
              homeHudNumber: construction.homeHudNumber ?? undefined,
              skirtingType: construction.skirtingType ?? undefined,
            }
          : {}),
        ...(details
          ? {
              communityName: details.communityName
                ? { label: details.communityName, value: details.communityName }
                : undefined,
              location: details.location ?? undefined,
            }
          : {}),
      };
    },
    selectHomeDetailsFormDefaultValues(state): HomeDetailsForm {
      if (state.unitsDetails.length === 0) {
        return {
          homeFixtures: undefined,
          homeType: undefined,
          roofShape: undefined,
          roofYearYyyy: undefined,
          manufacturer: undefined,
          makeCustom: undefined,
          modelHome: undefined,
          modelYear: undefined,
          totalSquareFootage: undefined,
          homeWidth: undefined,
          homeLength: undefined,
          homeHudNumber: undefined,
          skirtingType: undefined,
          communityName: undefined,
          location: undefined,
        };
      }

      const { construction, details } = state.unitsDetails[0];

      return {
        ...(construction
          ? {
              homeFixtures: construction.homeFixtures ?? undefined,
              homeType: construction.homeType ?? undefined,
              roofShape: construction.roofShape ?? undefined,
              roofYearYyyy: construction.roofYearYyyy ?? undefined,
              manufacturer: construction.manufacturerName
                ? {
                    label: construction.manufacturerName,
                    value: construction.manufacturerName,
                  }
                : undefined,
              modelYear: construction.modelYear
                ? {
                    label: construction.modelYear.toString(),
                    value: construction.modelYear,
                  }
                : undefined,
              makeCustom: construction.makeCustom ?? undefined,
              modelHome: construction.modelHome ?? undefined,
              totalSquareFootage: construction.totalSquareFootage ?? undefined,
              homeWidth: construction.homeWidth ?? undefined,
              homeLength: construction.homeLength ?? undefined,
              homeHudNumber: construction.homeHudNumber ?? undefined,
              skirtingType: construction.skirtingType ?? undefined,
            }
          : {}),
        ...(details
          ? {
              communityName: details.communityName
                ? { label: details.communityName, value: details.communityName }
                : undefined,
              location: details.location ?? undefined,
            }
          : {}),
      };
    },
  },
  reducers: {
    /**
     * Sets unit's construction data in state
     * @param state
     * @param payload
     */
    setUnitsConstruction: (
      state,
      {
        payload,
      }: PayloadAction<{
        unitIndex: number;
        unitConstruction: UnitConstructionInput;
      }>
    ) => {
      if (state.unitsDetails.length <= payload.unitIndex) {
        state.unitsDetails.push({
          construction: payload.unitConstruction,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          details: {},
        });
        return;
      }

      state.unitsDetails[payload.unitIndex].construction =
        payload.unitConstruction;
    },
    /**
     * Sets unit's details data in state
     * @param state
     * @param payload
     */
    setUnitsDetails: (
      state,
      {
        payload,
      }: PayloadAction<{ unitIndex: number; unitDetails: UnitDetailsInput }>
    ) => {
      if (state.unitsDetails.length <= payload.unitIndex) {
        state.unitsDetails.push({
          construction: {},
          details: payload.unitDetails,
        });
        return;
      }

      state.unitsDetails[payload.unitIndex].details = payload.unitDetails;
    },
    /**
     *
     * Set Unit Construction and Details based on Policy details in CYQ
     */
    setUnitsConstructionAndDetailsBasedOnPolicy: (
      state,
      {
        payload,
      }: PayloadAction<Pick<ContinueQuotePolicyFragmentFragment, "units">>
    ) => {
      // TODO fix for multi-unit policies
      if (state.unitsDetails.length === 0) {
        state.unitsDetails.push({
          construction: omit(payload.units[0].construction, [
            "roofCondition",
            "__typename",
          ]),
          details: {
            ...(payload.units[0].details.communityName
              ? { communityName: payload.units[0].details.communityName }
              : { communityName: undefined }),
            location: payload.units[0].details.unitLocation,
          },
        });
        return;
      }
      state.unitsDetails[0].details = {
        ...(payload.units[0].details.communityName
          ? { communityName: payload.units[0].details.communityName }
          : { communityName: undefined }),
        location: payload.units[0].details.unitLocation,
      };
      state.unitsDetails[0].construction = omit(payload.units[0].construction, [
        "roofCondition",
        "__typename",
      ]);
    },
    /**
     * Resets state to initial value
     */
    resetUnitConstructionAndDetails: () => initialState,
  },
});

export const {
  actions: {
    setUnitsConstruction,
    setUnitsDetails,
    resetUnitConstructionAndDetails,
    setUnitsConstructionAndDetailsBasedOnPolicy,
  },
  selectors: {
    selectHomeDetailsFormDefaultValues,
    selectLocationAndRoofDetailsFormDefaultValues,
    selectUnitDetails,
  },
} = unitsConstructionAndDetailsSlice;

/**
 * Draft Selector, which grabs state of whole slice needed to API call
 * Can be used within reducers of other slices
 */
export const getUnitConstructionAndDetails = createDraftSafeSelector(
  selectUnitDetails,
  (unitDetails) => unitDetails
);
